import { FC, useState, useRef } from 'react';
import { useLoaderData, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import type { Belt, PaginatedResponse } from '../types';

// CSS Structure
import CommonWrapper from '../components/ui_components/CommonComponentsStructureCss';

// Componenti
import SearchPpv from '../components/ppv_components/SearchPpv';
import BoxPage from '../components/ppv_components/BoxPage';
import { apiListCintureForAlbo } from '../components/ui_components/ApiList';
import { LoadingState } from '../components/stats_components/LoadingState';
import colors from '../styles/colors';
import { TopDecorColor } from '../components/specific_ppv_year_info_component/single_match_card_in_info_ppv_folder/GenericComponentCssMatchCard';

const ContainerListBelts = styled.div`
    width: 60%;
    margin: 0 auto;
`;

const TopSize = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
`;

const ListBeltsContainer = styled.div`
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    justify-content: space-between;
    align-items: center;
`;

const BeltCard = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: calc(50% - 15px);
    margin: 0 auto;
    padding: 30px 0 10px 0;
    border: 4px solid ${colors.bgColorBlack2};
    border-radius: 8px;
    background-color: ${colors.bgColorBordeaux};
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 4px 8px rgba(0,0,0,0.15);
    }

    img.belt-image {
        width: 90%;
        height: 200px;
        object-fit: contain;
        border-radius: 4px;
        cursor: pointer;
    }

    h3.belt-name {
        margin: 0.5rem 0;
        color: white;
        font-size: 20px;
        font-weight: 600;
    }

    p {
        color: #666;
        margin: 0.5rem 0;
        font-size: 0.9rem;
    }
`;

interface BeltListItem extends Belt {
    current_champion?: string;
    is_team?: boolean;
}

const ListBelts: FC = () => {
    const loaderData = useLoaderData() as PaginatedResponse<BeltListItem>;
    const [responseData, setResponseData] = useState<PaginatedResponse<BeltListItem>>(loaderData);
    const [isLoading, setIsLoading] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);
    const navigate = useNavigate();

    const updateListBeltsWithSearchBar = async (value: string): Promise<void> => {
        if (abortControllerRef.current) abortControllerRef.current.abort();
        abortControllerRef.current = new AbortController();
        const { signal } = abortControllerRef.current;
        try {
            setIsLoading(true);
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}api/belts/search?query=${value}`, { signal });
            const data = await response.json();
            if (!signal.aborted) setResponseData(data);
        } catch (error) {
            if (error instanceof Error && error.name !== 'AbortError') {
                console.error('Error during API request:', error);
            }
        } finally {
            if (!signal.aborted) setIsLoading(false);
        }
    };

    const updateDataByPage = async (page: number): Promise<void> => {
        if (abortControllerRef.current) abortControllerRef.current.abort();
        abortControllerRef.current = new AbortController();
        const { signal } = abortControllerRef.current;
        try {
            setIsLoading(true);
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}api/belts?page=${page}`, { signal });
            const data = await response.json();
            if (!signal.aborted) setResponseData(data);
        } catch (error) {
            if (error instanceof Error && error.name !== 'AbortError') {
                console.error('Error during API request:', error);
            }
        } finally {
            if (!signal.aborted) setIsLoading(false);
        }
    };

    const handleBeltClick = (belt: BeltListItem): void => {
        if (belt.is_team) {
            navigate(`/belts/tag/${belt.id}/hall-of-fame`);
        } else {
            navigate(`/belts/${belt.id}/hall-of-fame`);
        }
    };

    if (isLoading) {
        return <LoadingState />;
    }

    return (
        <CommonWrapper className="wrapper-ListBelts">
            <ContainerListBelts className='main-container-list-belts'>

                <TopSize className="row TopSize">
                    <SearchPpv
                        listPpv={responseData.data}
                        onFilterTextChange={updateListBeltsWithSearchBar}
                    />

                    <BoxPage
                        className="col-4"
                        maxPageApi={responseData.last_page}
                        currentPage={responseData.current_page}
                        onPageChange={updateDataByPage}
                    />
                </TopSize>

                <ListBeltsContainer className="ListBeltsContainer">
                    {responseData.data.map((belt, index) => (
                        <BeltCard className="belt-card" key={index}>
                            <TopDecorColor />
                            <img
                                src={process.env.REACT_APP_STORAGE_URL + belt.img_path}
                                alt={belt.name}
                                className="belt-image"
                                onClick={() => handleBeltClick(belt)}
                            />
                            <h3 className="belt-name">{belt.name}</h3>
                            {belt.current_champion && (
                                <p>Campione attuale: {belt.current_champion}</p>
                            )}
                        </BeltCard>
                    ))}
                </ListBeltsContainer>

            </ContainerListBelts>
        </CommonWrapper>
    );
};

export async function loader() {
    const response = await apiListCintureForAlbo() as unknown as { error?: string; data?: unknown };
    if (response.error) {
        return response.error;
    } else {
        return response.data;
    }
}

export default ListBelts;
